import { combine, createEffect, createEvent, createStore, merge, sample } from 'effector';
import { debounce } from 'patronum';
import { api, unwrap, type Schemas } from '../../shared/api/client';
import { latest } from '../../shared/lib/latest';

export type Alert = Schemas['AlertOut'];
export type AlertFilter = 'open' | 'all';

export const alertsRefreshed = createEvent();
export const filterChanged = createEvent<AlertFilter>();

export const loadOpenAlertsFx = createEffect(() =>
  unwrap(api.GET('/api/v1/alerts', { params: { query: { status: 'open' } } })),
);

export const loadAlertsFx = createEffect((status: AlertFilter) =>
  unwrap(api.GET('/api/v1/alerts', { params: { query: { status } } })),
);

export const dismissAlertFx = createEffect((id: number) =>
  unwrap(api.POST('/api/v1/alerts/{alert_id}/dismiss', { params: { path: { alert_id: id } } })),
);

/** Refreshes asked for at the same moment (a change and its live event) are merged. */
const COALESCE_MS = 50;
const open = latest(loadOpenAlertsFx);
const every = latest(loadAlertsFx);

export const $openAlerts = createStore<Alert[]>([]).on(open.done, (_, list) => list);
const $openLoaded = createStore(false).on(open.done, () => true);
const $allAlerts = createStore<Alert[] | null>(null).on(every.done, (_, list) => list);
export const $filter = createStore<AlertFilter>('open').on(filterChanged, (_, f) => f);

/** The Alerts page list: the open alerts are already loaded, so "Open" never fetches twice. */
export const $alerts = combine(
  $filter,
  $openAlerts,
  $openLoaded,
  $allAlerts,
  (filter, openList, openLoaded, all): Alert[] | null =>
    filter === 'open' ? (openLoaded ? openList : null) : all,
);

const refreshWanted = debounce(merge([alertsRefreshed, dismissAlertFx.done]), COALESCE_MS);
sample({ clock: refreshWanted, target: open.run });
sample({
  clock: [refreshWanted, filterChanged],
  source: $filter,
  filter: (filter) => filter === 'all',
  fn: (): AlertFilter => 'all',
  target: every.run,
});
